import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "./CustomAreaSelect";

export default function AreaSelect(props) {
  const map = useMap();
  const initializedRef = useRef(false);
  const listenersSetupRef = useRef(false);

  useEffect(() => {
    map.invalidateSize(true);
    map.getRenderer(map).options.padding = 100;

    const maxLat = 90;
    const minLat = -90;
    const bounds = L.latLngBounds(
      L.latLng(minLat, -Infinity),
      L.latLng(maxLat, Infinity)
    );
    map.setMaxBounds(bounds);

    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'
    ).addTo(map);
  }, [map]);

  useEffect(() => {
    if (!map.customSelectArea) {
      return;
    }

    const extractBounds = (e) => {
      if (!e || !e.bounds) return null;
      
      let northEast, southWest;
      if (typeof e.bounds.getNorthEast === 'function') {
        northEast = e.bounds.getNorthEast();
        southWest = e.bounds.getSouthWest();
      } else if (e.bounds._northEast && e.bounds._southWest) {
        northEast = e.bounds._northEast;
        southWest = e.bounds._southWest;
      } else {
        return null;
      }
      
      if (!northEast || !southWest) return null;
      
      return {
        north: northEast.lat,
        south: southWest.lat,
        east: northEast.lng,
        west: southWest.lng,
      };
    };

    const handleAreaSelectStart = (e) => {
      if (!props.isSelecting) return;
      const bounds = extractBounds(e);
      if (bounds) {
        props.onBoundsUpdate(bounds);
      }
    };

    const handleAreaSelected = (e) => {
      if (!props.isSelecting) return;
      const bounds = extractBounds(e);
      if (bounds) {
        props.onBoundsUpdate(bounds);
      }
    };

    if (!listenersSetupRef.current) {
      map.on("areaselectstart", handleAreaSelectStart);
      map.on("areaselected", handleAreaSelected);
      listenersSetupRef.current = true;
    }

    return () => {
      if (listenersSetupRef.current) {
        map.off("areaselectstart", handleAreaSelectStart);
        map.off("areaselected", handleAreaSelected);
        listenersSetupRef.current = false;
      }
    };
  }, [map, props.isSelecting]);

  useEffect(() => {
    if (!map.customSelectArea) {
      return;
    }

    const parseCoord = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const northLat = parseCoord(props.bounds?.[0]?.[0]);
    const eastLong = parseCoord(props.bounds?.[0]?.[1]);
    const southLat = parseCoord(props.bounds?.[1]?.[0]);
    const westLong = parseCoord(props.bounds?.[1]?.[1]);
    const hasCompleteBounds = [northLat, eastLong, southLat, westLong].every(
      (coord) => coord !== null
    );

    if (hasCompleteBounds) {
      const boundsToUse = L.latLngBounds(
        L.latLng(northLat, eastLong),
        L.latLng(southLat, westLong)
      );

      if (!map.customSelectArea._rectangle) {
        map.customSelectArea.drawArea(boundsToUse);
      } else {
        const isUserInteracting = map.customSelectArea._cornerPressed || map.customSelectArea._rectanglePressed;
        
        if (!isUserInteracting) {
          const currentBounds = map.customSelectArea._rectangle.getBounds();
          const currentNorthEast = currentBounds.getNorthEast();
          const currentSouthWest = currentBounds.getSouthWest();
          const newNorthEast = boundsToUse.getNorthEast();
          const newSouthWest = boundsToUse.getSouthWest();
          
          const boundsChanged = 
            Math.abs(currentNorthEast.lat - newNorthEast.lat) > 0.00001 ||
            Math.abs(currentNorthEast.lng - newNorthEast.lng) > 0.00001 ||
            Math.abs(currentSouthWest.lat - newSouthWest.lat) > 0.00001 ||
            Math.abs(currentSouthWest.lng - newSouthWest.lng) > 0.00001;
          
          if (boundsChanged) {
            map.customSelectArea._suppressEvents = true;
            map.customSelectArea.updateArea(boundsToUse);
            map.customSelectArea._suppressEvents = false;
          }
        }
      }
      
      map.customSelectArea._allowInteraction = props.isSelecting;
    } else {
      if (props.isSelecting) {
        if (!map.customSelectArea._rectangle) {
          const boundsToUse = map.getBounds().pad(-0.25);
          map.customSelectArea.drawArea(boundsToUse);
        }
        map.customSelectArea._allowInteraction = true;
      } else {
        if (map.customSelectArea._rectangle) {
          map.customSelectArea.reset();
        }
      }
    }
  }, [map, props.isSelecting, props.bounds]);

  return null;
}
